import { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Landing() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [position, setPosition] = useState(null);

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!isValidEmail(value)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email: value });
      setPosition(data.position);
      setStatus("success");
      toast.success("You're on the list. Welcome to ARIA.");
    } catch (err) {
      setStatus("idle");
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    }
  };

  return (
    <main
      data-testid="landing-page"
      className="relative flex min-h-screen w-full flex-col overflow-hidden bg-[#161616] text-[#ededee]"
    >

      {/* Top bar */}
      <header className="relative z-10 mx-auto flex w-full max-w-[1680px] shrink-0 items-center justify-between px-8 pt-8 sm:px-16 sm:pt-10">
        <div className="aria-mark aria-fade" data-testid="aria-logo">
          <img
            src="/aria-logo.png"
            alt="ARIA"
            className="aria-logo-img h-12 w-auto sm:h-16"
            draggable={false}
          />
          <span className="text-[18px] font-medium tracking-[0.28em] text-white/95 sm:text-[22px]">
            ARIA
          </span>
        </div>
        <a
          href="mailto:info@ariatechnologies.ai"
          data-testid="contact-link"
          className="hidden text-[14px] tracking-wide text-white/45 transition-colors hover:text-white/80 sm:inline-block sm:text-[15px]"
        >
          info@ariatechnologies.ai
        </a>
      </header>

      {/* Main content — grows to fill viewport, content vertically centered */}
      <section className="relative z-10 mx-auto flex w-full max-w-[1680px] flex-1 flex-col items-start justify-center px-8 py-16 sm:px-16 sm:py-20">
        <div
          className="aria-rise mb-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 backdrop-blur-md"
          data-testid="coming-soon-badge"
          style={{ animationDelay: "60ms" }}
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#8b7cff] opacity-60" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#8b7cff]" />
          </span>
          <span className="text-[11px] uppercase tracking-[0.28em] text-white/65">
            Launching Soon
          </span>
        </div>

        <h1
          className="aria-rise max-w-[1200px] text-balance text-5xl font-semibold leading-[1.02] tracking-[-0.025em] text-white sm:text-6xl lg:text-[88px] xl:text-[104px]"
          data-testid="hero-heading"
          style={{ animationDelay: "160ms" }}
        >
          The <span className="aria-serif text-white/95">AI</span> for your money.
        </h1>

        <p
          className="aria-rise mt-8 max-w-3xl text-[16px] leading-relaxed text-white/55 sm:text-[18px] lg:text-[20px]"
          data-testid="hero-subtitle"
          style={{ animationDelay: "260ms" }}
        >
          ARIA is your AI-powered financial intelligence layer — built to
          optimise wealth, maximise returns and help you make better financial
          decisions.
        </p>

        {/* Form */}
        <div
          className="aria-rise mt-12 w-full max-w-2xl"
          style={{ animationDelay: "360ms" }}
        >
          {status === "success" ? (
            <div
              data-testid="success-message"
              className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md"
            >
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#8b7cff]/40 bg-[#8b7cff]/15">
                <Check className="h-3.5 w-3.5 text-[#b9b0ff]" />
              </div>
              <div className="flex-1">
                <p className="text-[15px] text-white">You're on the list.</p>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="waitlist-form"
              className="aria-input flex items-center gap-2 rounded-full p-1.5 pl-5"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@domain.com"
                data-testid="waitlist-email-input"
                disabled={status === "loading"}
                autoComplete="email"
                className="h-11 flex-1 text-[15px]"
                required
              />
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="waitlist-submit-button"
                className="aria-pill group inline-flex h-11 items-center gap-2 px-5 text-[14px] font-medium text-white disabled:opacity-60"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Joining
                  </>
                ) : (
                  <>
                    Request access
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>
          )}

          <p
            className="mt-4 text-[12px] tracking-wide text-white/35"
            data-testid="form-helper"
          >
            No spam. We email you only when ARIA is ready for you.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 mx-auto w-full max-w-[1680px] shrink-0 px-8 pb-8 sm:px-16">
        <div className="flex items-center justify-between border-t border-white/[0.06] pt-6 text-[12px] text-white/35">
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} ARIA Technologies
          </span>
          <span className="hidden sm:inline">Crafted for those who measure things twice.</span>
        </div>
      </footer>
    </main>
  );
}
